"use client";
import { Dialog } from "@base-ui/react/dialog";
import { Separator } from "@base-ui/react/separator";
import { XIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";
import verified from "#/assets/badge/verified-badge.svg";
import { getSession } from "@/actions/auth.action";
import { interactionLog } from "@/actions/consultation.action";
import type { Consultant } from "@/types/consulting";
import { Button } from "../ui/button";
import ExtendedImage from "../ui/extended-image";

const WhatsAppIcon = () => (
  <svg
    aria-hidden="true"
    fill="currentColor"
    height="20"
    viewBox="0 0 24 24"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

interface ExpertDialogProps {
  children: ReactNode;
  expert: Consultant;
}

const ExpertDialog = ({ children, expert }: ExpertDialogProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const router = useRouter();

  useEffect(() => {
    getSession().then((session) => setIsLoggedIn(session.isLoggedIn));
  }, []);

  const whatsappHref = `https://wa.me/${expert.phoneNumber.replace(/\D/g, "")}`;

  return (
    <Dialog.Root>
      <Dialog.Trigger className="appearance-none border-none bg-transparent p-0 m-0 text-left cursor-pointer">
        {children}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity data-starting-style:opacity-0 data-ending-style:opacity-0" />
        <Dialog.Popup className="fixed top-1/2 left-1/2 z-50 flex w-[90vw] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl bg-white shadow-2xl outline-none max-h-[90vh] overflow-y-auto transition-all duration-150 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0">
          <div className="flex items-center justify-between px-6 py-4">
            <Dialog.Title className="text-xl font-bold text-gray-900">
              {expert.name}
            </Dialog.Title>
            <Dialog.Close className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
              <XIcon className="size-5" />
            </Dialog.Close>
          </div>

          <Separator className="bg-gray-200 h-px" orientation="horizontal" />

          <div className="flex items-center gap-4 px-6 py-5">
            <ExtendedImage
              alt={expert.name}
              className="rounded-full size-16 object-cover shrink-0"
              height={64}
              src={expert.imageUrl}
              width={64}
            />
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900">
                {expert.name}
                <ExtendedImage
                  alt="verified"
                  height={16}
                  src={verified}
                  width={16}
                />
              </span>
              <span className="text-sm text-gray-500">{expert.title}</span>
            </div>
          </div>

          <Separator className="bg-gray-200 h-px" orientation="horizontal" />

          <div className="flex items-center gap-3 px-6 py-4">
            <ExtendedImage
              alt={expert.schoolName}
              className="rounded-full size-10 grow-0 aspect-square object-cover shrink-0"
              height={40}
              src={expert.schoolImageUrl}
              width={40}
            />
            <span className="text-sm font-medium text-gray-700">
              {expert.schoolName}
            </span>
          </div>

          <Separator className="bg-gray-200 h-px" orientation="horizontal" />

          <p className="px-6 py-5 text-sm leading-6 text-gray-700">
            {expert.bio}
          </p>

          <div className="px-6 pb-6">
            {isLoggedIn ? (
              <a
                className="flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] py-3 text-white font-semibold text-base transition-opacity hover:opacity-90"
                href={whatsappHref}
                onClick={() =>
                  interactionLog({
                    target: { slug: expert.slug, type: "consultant" },
                    type: "whatsapp-click",
                  })
                }
                rel="noreferrer"
                target="_blank"
              >
                <WhatsAppIcon />
                Whatsapp
              </a>
            ) : (
              <Button
                className="w-full"
                onClick={() => router.push("/auth/login")}
              >
                İletişime geçmek için giriş yap
              </Button>
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExpertDialog;
